/**
 * Idempotency keys for money-moving requests (listing publish / renew, agent add-on enable) — BR-91.
 *
 * A charge whose outcome the landlord never learned (network error, timeout, ambiguous 5xx, a
 * charge still in progress, an unreconciled earlier attempt) may have landed. Retrying it under a
 * NEW key could charge twice, so the key survives a page reload or a closed-and-reopened dialog
 * (sessionStorage, per listing/subscription scope) until the attempt reaches a DEFINITIVE outcome.
 *
 * A definitive outcome — paid, declined, consent mismatch, not eligible, ... — rotates the key: a
 * reused key would replay the old decline, so a new card must be charged under a new key. (A consent
 * mismatch against the PINNED quote of an earlier unresolved attempt is not definitive: it keeps the key.)
 *
 * Storage is best-effort: every access is guarded and an in-memory copy keeps the key stable for the
 * life of the page when storage is unavailable (private window, blocked site data).
 */

import { ApiClientError } from './api.core';

const STORAGE_PREFIX = 'resida:charge-key:';

/** 409/5xx codes whose outcome is unknown: the charge may have landed, so the key must be reused. */
const UNRESOLVED_CODES = new Set([
  'CHARGE_IN_PROGRESS',
  'CHARGE_OUTCOME_UNKNOWN',
  'LISTING_FEE_UNRECONCILED',
  'ADDON_CHARGE_UNRECONCILED',
  'SERVICE_UNAVAILABLE',
  // The fresh-session guard rejected the request before any charge. The shared re-authentication flow
  // normally swallows it (the call never settles, so the key survives); if it ever surfaces as an error
  // the key is still kept: the user re-authenticates and retries the same attempt.
  'REAUTHENTICATION_REQUIRED',
]);

/**
 * Codes the server returns BEFORE or INSTEAD of any charge: the attempt is over, whatever the status.
 * `CHARGE_REJECTED` is only ever answered for a FIRST attempt (nothing unresolved under the key). When
 * billing rejects the same-key RETRY of an unresolved attempt on its live amount bound, the server keeps
 * the claim and answers `*_UNRECONCILED` instead (BR-91/BR-102: the earlier charge may have landed), which
 * is in UNRESOLVED_CODES above — so the key survives and no second charge can be started under a new one.
 */
const DEFINITIVE_CODES = new Set([
  'PAYMENT_DECLINED',
  'FEE_CONSENT_REQUIRED',
  'PAYMENT_METHOD_REQUIRED',
  'LISTING_FEE_NOT_OFFERED',
  'ADDON_NOT_ELIGIBLE',
  'ADDON_ALREADY_ACTIVE',
  'CHARGE_REJECTED',
  'RENEWAL_NOT_AVAILABLE',
  'ADDON_INVALID_STATE',
]);

const memory = new Map<string, string>();

function isResumingQuote(quote: unknown): boolean {
  return typeof quote === 'object' && quote !== null && (quote as { resumesEarlierAttempt?: unknown }).resumesEarlierAttempt === true;
}

/** Shown when the API rate-limits a money request (429): nothing was charged by that request. */
export const RATE_LIMITED_MESSAGE = 'Too many attempts. Please wait a moment and try again.';

export function isRateLimitedError(err: unknown): boolean {
  return err instanceof ApiClientError && err.status === 429;
}

/**
 * True when the request's outcome is unresolved and a retry MUST reuse the same key:
 *  - not an API response at all (network error, timeout, aborted fetch, a non-JSON proxy error)
 *  - any 5xx, and 408 (request timeout)
 *  - 429 (rate limited): rejected before the charge ran, but a same-key earlier attempt may still be
 *    outstanding, so the key is kept (reusing a key can never double-charge)
 *  - a known unresolved code (see UNRESOLVED_CODES), and any OTHER 409 (an unknown conflict is
 *    treated conservatively: reusing a key can never double-charge)
 * Everything else (402 decline, 400 consent mismatch, 403 not eligible, 404, other 4xx, and the
 * 409s in DEFINITIVE_CODES) is a definitive answer: the server decided with no charge outstanding.
 */
export function isUnresolvedChargeError(err: unknown): boolean {
  if (!(err instanceof ApiClientError)) return true;
  // A consent mismatch against the PINNED quote of an earlier unresolved attempt (BR-91/BR-102): that
  // attempt is still outstanding under this key, so the key must survive to resume it — rotating it would
  // start a second charge the server refuses (or, worse, race the first).
  if (err.code === 'FEE_CONSENT_REQUIRED' && isResumingQuote((err.details as { quote?: unknown } | undefined)?.quote)) {
    return true;
  }
  if (DEFINITIVE_CODES.has(err.code)) return false;
  if (UNRESOLVED_CODES.has(err.code)) return true;
  return err.status >= 500 || err.status === 408 || err.status === 409 || err.status === 429;
}

function storageKey(scope: string): string {
  return `${STORAGE_PREFIX}${scope}`;
}

function readStored(scope: string): string | null {
  try {
    return window.sessionStorage.getItem(storageKey(scope));
  } catch {
    return null;
  }
}

/**
 * The persisted key of this scope's UNRESOLVED attempt, or null when there is none. Never creates a key:
 * the quote reads it so the server can return the pinned quote of that attempt (BR-91/BR-102).
 */
export function peekChargeIdempotencyKey(scope: string): string | undefined {
  return readStored(scope) ?? memory.get(scope) ?? undefined;
}

/** The key for this scope's current attempt: the persisted one when unresolved, else a new one. */
export function getChargeIdempotencyKey(scope: string): string {
  const stored = readStored(scope);
  const key = stored ?? memory.get(scope) ?? crypto.randomUUID();
  memory.set(scope, key);
  if (stored !== key) {
    try {
      window.sessionStorage.setItem(storageKey(scope), key);
    } catch {
      // Storage unavailable: the in-memory copy still keeps the key stable for this page.
    }
  }
  return key;
}

/**
 * Record how the attempt ended. `err` undefined means it succeeded. A definitive outcome clears the
 * scope's key (the next attempt gets a new one); an unresolved one keeps it.
 */
export function settleChargeIdempotencyKey(scope: string, err?: unknown): void {
  if (err !== undefined && isUnresolvedChargeError(err)) return;
  memory.delete(scope);
  try {
    window.sessionStorage.removeItem(storageKey(scope));
  } catch {
    // Nothing persisted, nothing to clear.
  }
}
